import { useReducer, useRef } from 'react';
import Observable from 'zen-observable';
import { registerMeetingCompleteSubscription } from '../config/appsync';
import { OnRegisterToMeetingCompletedSubscription } from '../config/appsync/API';
import { Hub } from 'aws-amplify';
import { AttendeeUrlProcessingPolling } from '../lib/services/polling/attendee-url-processing-polling';
import { THIRTY_SECONDS_TIMEOUT } from '../lib/constants';

type SubscriptionData = { value: { data: OnRegisterToMeetingCompletedSubscription }};

export enum RegistrationResultType {
  BOOKED = 'Booked',
  UNAVAILABLE = 'Unavailable',
  FAILED = 'Failed',
  REMOVED = 'Removed'
}

export type RegistrationResult = RegistrationResultType | null;

export type BookingSubscriptionState = {
  inProgress: boolean,
  result: RegistrationResult,
  error: string | null
}

export enum ACTION_TYPE {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

type ACTION =
  | { type: ACTION_TYPE.INITIALIZED }
  | { type: ACTION_TYPE.COMPLETED; payload: { result: RegistrationResult } }
  | { type: ACTION_TYPE.FAILED, payload?: string }
  ;

export type BookingResponse = { registrationId: string, appSyncToken: string };

export type BookingSubscriptionResult = {
  state: BookingSubscriptionState,
  subscribe(response: BookingResponse): Promise<void>
}

const initialState: BookingSubscriptionState = {
  inProgress: false,
  result: null,
  error: null
};

export function reducer(state: BookingSubscriptionState, action: ACTION): BookingSubscriptionState {
  switch (action.type) {
  case ACTION_TYPE.INITIALIZED:
    return { ...initialState, inProgress: true };
  case ACTION_TYPE.COMPLETED:
    return { ...initialState, result: action.payload.result };
  case ACTION_TYPE.FAILED:
    return { ...initialState, result: RegistrationResultType.FAILED, error: action.payload || null };
  default:
    return initialState;
  }
}

export const useBookingSubscription = (onSubscriptionStart: () => void): BookingSubscriptionResult => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const currentRegistrationId = useRef<string | null>(null);
  const activePolling = useRef<AttendeeUrlProcessingPolling | null>(null);
  const activeClient = useRef<{ unsubscribe(): void, closed: boolean } | null>(null);
  const hubListenerRemover = useRef<(() => void) | null>(null);
  const timeoutId = useRef<number | null>(null);

  const clearTimeoutIfExists = () => {
    if (timeoutId.current) {
      window.clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  const stopActivePolling = () => {
    if (activePolling.current) {
      activePolling.current.stop();
      activePolling.current = null;
    }
  };

  const cleanupPreviousSubscription = () => {
    stopActivePolling();
    clearTimeoutIfExists();

    if (activeClient.current && !activeClient.current.closed) {
      activeClient.current.unsubscribe();
      activeClient.current = null;
    }

    if (hubListenerRemover.current) {
      hubListenerRemover.current();
      hubListenerRemover.current = null;
    }
  };

  const isCurrentRegistration = (registrationId: string) =>
    currentRegistrationId.current === registrationId;

  const subscriptionCompleted = (result: RegistrationResult) => {
    dispatch({ type: ACTION_TYPE.COMPLETED, payload: { result }});
  };

  const subscriptionFailed = (payload?: string) => {
    dispatch({ type: ACTION_TYPE.FAILED, payload });
  };

  const startPollingFallback = (response: BookingResponse, resolve: () => void): void => {
    stopActivePolling();

    const polling = new AttendeeUrlProcessingPolling(
      response.registrationId,
      response.appSyncToken
    );
    activePolling.current = polling;

    polling.start()
      .then(pollingResponse => {
        if (!isCurrentRegistration(response.registrationId)) return;
        clearTimeoutIfExists();
        subscriptionCompleted(pollingResponse.registrationResult as RegistrationResult);
      })
      .catch(() => {
        if (!isCurrentRegistration(response.registrationId)) return;
        clearTimeoutIfExists();
        subscriptionFailed('Polling failed');
      });

    polling.subscribeToPollingStart(resolve);
  };

  const initSubscription = (client: { unsubscribe(): void, closed: boolean }) => {
    onSubscriptionStart();
    dispatch({ type: ACTION_TYPE.INITIALIZED });

    timeoutId.current = window.setTimeout(() => {
      if (!client.closed) client.unsubscribe();
      subscriptionFailed();
    }, THIRTY_SECONDS_TIMEOUT);
  };

  return {
    state,
    async subscribe(response: BookingResponse): Promise<void> {
      return new Promise((resolve) => {
        if (!response?.registrationId || !response?.appSyncToken) return;

        cleanupPreviousSubscription();
        currentRegistrationId.current = response.registrationId;

        const client = (registerMeetingCompleteSubscription(
          response.registrationId,
          response.appSyncToken
        ) as unknown as Observable<SubscriptionData>)
          .subscribe({
            next({ value }: SubscriptionData): void {
              if (!isCurrentRegistration(response.registrationId)) {
                client.unsubscribe();
                return;
              }

              const registrationResult = value?.data?.onRegisterToMeetingCompleted?.registrationResult as RegistrationResult;
              if (registrationResult) {
                clearTimeoutIfExists();
                subscriptionCompleted(registrationResult);
              }

              client.unsubscribe();
            },
            error(): void {
              if (!isCurrentRegistration(response.registrationId)) return;
              clearTimeoutIfExists();
              if (!client.closed) client.unsubscribe();
              startPollingFallback(response, resolve);
            }
          });

        activeClient.current = client;
        initSubscription(client);

        hubListenerRemover.current = Hub.listen('api', ({ payload }) => {
          const { event, data } = payload;

          if (event === 'Subscription ack') resolve();

          if (data?.connectionState === 'ConnectionDisrupted') {
            if (!isCurrentRegistration(response.registrationId)) return;
            clearTimeoutIfExists();
            if (!client.closed) client.unsubscribe();
            startPollingFallback(response, resolve);
          }
        });
      });
    }
  };
};
